import type { TrackError } from '@opendoor/auth-fe/context';

import type { useObservability } from '../../helpers/observability';
import { isRetryableZeusReadError } from '../api/zeusBrowserClient';

/**
 * `error.name` for a ViewerSession read that never got a response: the browser
 * cancelled it (navigation, tab close) or the network dropped it. auth-fe's
 * session failure report forwards only `error.name`, so this name is the
 * signal consumers use to route client transport noise away from Sentry. It
 * must stay a literal: bundlers mangle class names, so `<class>.name` cannot
 * be trusted.
 */
export const VIEWER_SESSION_TRANSPORT_ERROR_NAME = 'ViewerSessionTransportError';

// Transport errors can embed session credentials; report one scrubbed message
// for every session-read failure.
const VIEWER_SESSION_FAILURE_MESSAGE = 'ViewerSession request failed';

class ViewerSessionTransportError extends Error {
  constructor() {
    super(VIEWER_SESSION_FAILURE_MESSAGE);
    this.name = VIEWER_SESSION_TRANSPORT_ERROR_NAME;
  }
}

/**
 * Scrubbed failure for a rejected ViewerSession read. Fetch-level rejections
 * keep their transport class so `createTrackAuthError` can route them around
 * Sentry; everything else collapses to a generic error.
 */
export const sessionReadFailure = (error: unknown): Error =>
  isRetryableZeusReadError(error)
    ? new ViewerSessionTransportError()
    : new Error(VIEWER_SESSION_FAILURE_MESSAGE);

type ScopedTrackError = ReturnType<typeof useObservability>['trackError'];

/**
 * auth-fe reports every failed session read through `trackError`, and on
 * transient pages the browser usually cancels that read: /token hard-navigates
 * right after a magic-link redeem, so the failure is the unload, not Zeus.
 * Transport-class failures stay in Datadog RUM; response-level failures (HTTP
 * errors, GraphQL errors, session-shape mismatches) still reach Sentry.
 */
export const createTrackAuthError =
  (trackError: ScopedTrackError): TrackError =>
  (label, error, data) =>
    trackError(label, error, data, {
      skipSentry: data?.errorName === VIEWER_SESSION_TRANSPORT_ERROR_NAME,
    });
